import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Megaphone, Save } from "lucide-react";

const AD_TYPES = [
  { key: "hoarding", label: "Hoarding" },
  { key: "led_display", label: "LED Display" },
  { key: "banner", label: "Banner" },
  { key: "wall_wrap", label: "Wall Wrap" },
  { key: "kiosk", label: "Kiosk" },
  { key: "bus_shelter", label: "Bus Shelter" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function AdRegister() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isCitizen = user?.role === "citizen";
  const [form, setForm] = useState({
    advertiser_name: user?.name || "",
    business_name: "",
    advertiser_email: user?.email || "",
    advertiser_phone: user?.phone || "",
    ad_type: "hoarding", width_ft: 20, height_ft: 10,
    location_description: "", ward: "Ward-1", zone: "North",
    start_date: today(), duration_months: 12,
    municipal_body: "Ludhiana Municipal Corporation",
  });
  const [wards, setWards] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/master/wards").then((r) => setWards(r.data.items || []));
    api.get("/master/municipal-bodies").then((r) => setBodies(r.data.items || []));
  }, []);

  useEffect(() => {
    api.get("/advertisements/calculator", {
      params: { ad_type: form.ad_type, width_ft: Number(form.width_ft) || 1, height_ft: Number(form.height_ft) || 1, duration_months: Number(form.duration_months) || 12 },
    }).then((r) => setCalc(r.data));
  }, [form.ad_type, form.width_ft, form.height_ft, form.duration_months]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form,
        width_ft: Number(form.width_ft), height_ft: Number(form.height_ft),
        duration_months: Number(form.duration_months),
        start_date: new Date(form.start_date).toISOString(),
      };
      const r = await api.post("/advertisements", payload);
      toast.success("Advertisement registered", { description: r.data.ad_code });
      nav(isCitizen ? `/portal/advertisements/${r.data.id}` : `/advertisements/${r.data.id}`);
    } catch (err) {
      toast.error("Registration failed", { description: err.response?.data?.detail || "Try again" });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="ad-register-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Register</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-[#E8702A]" /> New Advertisement
        </h1>
      </div>

      <Card>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label>Advertiser Name *</Label>
            <Input value={form.advertiser_name} onChange={(e) => set("advertiser_name", e.target.value)} required data-testid="input-advertiser-name" />
          </div>
          <div>
            <Label>Business Name *</Label>
            <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} required data-testid="input-business-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.advertiser_email} onChange={(e) => set("advertiser_email", e.target.value)} />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.advertiser_phone} onChange={(e) => set("advertiser_phone", e.target.value)} />
          </div>
          <div>
            <Label>Ad Type *</Label>
            <Select value={form.ad_type} onValueChange={(v) => set("ad_type", v)}>
              <SelectTrigger data-testid="select-ad-type"><SelectValue /></SelectTrigger>
              <SelectContent>{AD_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Width (ft) *</Label>
              <Input type="number" value={form.width_ft} onChange={(e) => set("width_ft", e.target.value)} data-testid="input-width" />
            </div>
            <div>
              <Label>Height (ft) *</Label>
              <Input type="number" value={form.height_ft} onChange={(e) => set("height_ft", e.target.value)} data-testid="input-height" />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Location Description *</Label>
            <Input value={form.location_description} onChange={(e) => set("location_description", e.target.value)} placeholder="e.g., Ferozepur Road, near Model Town Chowk" required data-testid="input-location" />
          </div>
          <div>
            <Label>Ward *</Label>
            <Select value={form.ward} onValueChange={(v) => set("ward", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Zone</Label>
            <Select value={form.zone} onValueChange={(v) => set("zone", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["North", "South", "East", "West", "Central"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start Date *</Label>
            <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} required data-testid="input-start-date" />
          </div>
          <div>
            <Label>Duration (months) *</Label>
            <Input type="number" value={form.duration_months} min={1} max={60} onChange={(e) => set("duration_months", e.target.value)} data-testid="input-duration" />
          </div>
          <div>
            <Label>Municipal Body</Label>
            <Select value={form.municipal_body} onValueChange={(v) => set("municipal_body", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{bodies.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {calc && (
        <Card className="bg-[#E8702A] text-white" data-testid="ad-fee-card">
          <CardContent className="p-6 flex flex-wrap justify-between items-center gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80">Estimated Fee</div>
              <div className="text-4xl font-heading font-bold mt-1">{formatINR(calc.fee_amount)}</div>
            </div>
            <div className="text-sm opacity-90">
              {calc.sqft} sqft · {formatINR(calc.annual_rate_per_sqft)}/sqft/year · {calc.duration_months} months
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => nav(isCitizen ? "/portal" : "/advertisements")}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="btn-submit-ad">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Register Advertisement"}
        </Button>
      </div>
    </form>
  );
}
